import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Search, Users, Briefcase, Calendar, ChevronDown, ChevronUp, Globe,
  Languages, BookOpen, FileText, CheckCircle, Mail, Clock,
  Sparkles, Plus, Trash2, Send, RotateCcw, AlertCircle, Linkedin,
  Eye, TriangleAlert, ListChecks, Info, Layers, X,
  Film, MessageSquare, ArrowUp, ArrowDown, HelpCircle, Heart,
} from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'
import { evaluationsApi } from '../../lib/api/evaluations'
import { parseVtt } from '../../lib/utils/parseVtt'
import { buildRecordingSrc } from '../../lib/utils/streamUrl'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Client {
  user_id: number
  full_name: string | null
  email: string | null
  target_role: string | null
  total_sessions: number
  last_session_at: string | null
  timezone: string | null
  language: string | null
  linkedin_url: string | null
  experience_level: string | null
  interview_goals: string | null
  industry: string | null
  resume_url: string | null
}

interface QuestionType {
  id: number
  name: string
  slug: string
  description: string | null
  display_order: number
  is_active: boolean
}

interface QuestionItem {
  text: string
  source: string
  question_type_id?: number | null
  question_type_name?: string | null
  expected_answer?: string
}

interface QuestionSet {
  id: number
  session_id: number
  questions: QuestionItem[]
  generation_context: { target_role?: string; experience_level?: string } | null
  is_finalized: boolean
  created_at: string
}

interface SessionItem {
  id: number
  booking_id: number | null
  title: string
  status: string
  scheduled_at: string
  duration_minutes: number
  interviewer_alias: string | null
  zoom_join_url: string | null
}

interface RecordingMeta {
  id: number
  session_id: number | null
  booking_id: number | null
  recording_url: string | null
  transcript_url: string | null
  duration_seconds: number | null
  status: string
}

interface Note {
  id: number
  session_id: number
  content: string
  is_private: boolean
  created_at: string
}

interface QuestionTemplate {
  id: number
  title: string
  description?: string
  target_role?: string
  tier?: string
  questions: { text: string }[]
  is_active: boolean
}

// ── Constants ─────────────────────────────────────────────────────────────────


// ── Helpers ───────────────────────────────────────────────────────────────────

function formatShortDate(iso: string, tz?: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...(tz ? { timeZone: tz } : {}) })
}

function formatDateTime(iso: string, tz?: string) {
  const d = new Date(iso)
  const tzOpts = tz ? { timeZone: tz } : {}
  return {
    date: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...tzOpts }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...tzOpts }),
  }
}

function getDaysUntil(iso: string): number {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const target = new Date(iso)
  const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate())
  return Math.round((targetDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-dark)', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{children}</span>
    </div>
  )
}

// ── Question Set Builder (shared panel) ────────────────────────────────────────

function QuestionSetBuilder({
  sessionId: _sessionId,
  finalQuestions,
  setFinalQuestions,
  generatedQuestions,
  setGeneratedQuestions,
  generating,
  setGenerating,
  saving,
  saveMsg,
  onSave,
  clientUserId,
  existingQS,
  questionTypes,
  jobDescription,
}: {
  sessionId: number
  finalQuestions: QuestionItem[]
  setFinalQuestions: (qs: QuestionItem[]) => void
  generatedQuestions: QuestionItem[]
  setGeneratedQuestions: (qs: QuestionItem[]) => void
  generating: boolean
  setGenerating: (v: boolean) => void
  saving: boolean
  saveMsg: string
  onSave: () => void
  clientUserId: number
  existingQS: QuestionSet | null
  questionTypes: QuestionType[]
  jobDescription: string | null
}) {
  const [customQuestion, setCustomQuestion] = useState('')
  const [customQTypeId, setCustomQTypeId] = useState<number | ''>('')
  const [genError, setGenError] = useState('')
  // Per AI-question type selection: index → typeId
  const [aiQTypes, setAiQTypes] = useState<Record<number, number | ''>>({})
  // Per-question answer generation loading state (keyed by index in finalQuestions)
  const [answerGenerating, setAnswerGenerating] = useState<Record<number, boolean>>({})
  // Which final question's answer is expanded/editing
  const [answerExpanded, setAnswerExpanded] = useState<Record<number, boolean>>({})

  const handleGenerate = async () => {
    setGenerating(true)
    setGenError('')
    setGeneratedQuestions([])
    setAiQTypes({})
    const maxAttempts = 3
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const questions = await interviewerApi.generateQuestionsForClient(clientUserId, {
          num_questions: 8,
          job_description: jobDescription ?? null,
        })
        setGeneratedQuestions(questions || [])
        setGenError('')
        setGenerating(false)
        return
      } catch {
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, 800 * attempt))
        }
      }
    }
    setGenError('Failed to generate questions. Please try again.')
    setGenerating(false)
  }

  const addToFinal = (q: QuestionItem, idx: number) => {
    const selectedTypeId = aiQTypes[idx]
    if (!selectedTypeId) {
      // Highlight the select instead of alerting
      const el = document.getElementById(`ai-qtype-${idx}`)
      el?.focus()
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      return
    }
    if (finalQuestions.find((fq) => fq.text === q.text)) return
    const qt = questionTypes.find((t) => t.id === selectedTypeId)
    setFinalQuestions([...finalQuestions, {
      ...q,
      question_type_id: selectedTypeId,
      question_type_name: qt?.name ?? null,
    }])
  }

  const addCustom = () => {
    const text = customQuestion.trim()
    if (!text) return
    if (!customQTypeId) {
      document.getElementById('custom-qtype-select')?.focus()
      return
    }
    const qt = questionTypes.find((t) => t.id === customQTypeId)
    setFinalQuestions([...finalQuestions, {
      text,
      source: 'manual',
      question_type_id: customQTypeId || null,
      question_type_name: qt?.name ?? null,
    }])
    setCustomQuestion('')
    setCustomQTypeId('')
  }

  const remove = (idx: number) => {
    setFinalQuestions(finalQuestions.filter((_, i) => i !== idx))
    setAnswerExpanded((prev) => {
      const next = { ...prev }
      delete next[idx]
      return next
    })
  }

  const moveQuestion = (idx: number, direction: -1 | 1) => {
    const target = idx + direction
    if (target < 0 || target >= finalQuestions.length) return
    const reordered = [...finalQuestions]
    ;[reordered[idx], reordered[target]] = [reordered[target], reordered[idx]]
    setFinalQuestions(reordered)
    setAnswerExpanded((prev) => {
      const next: Record<number, boolean> = {}
      for (const k of Object.keys(prev)) {
        const ki = parseInt(k)
        if (ki === idx) next[target] = prev[ki]
        else if (ki === target) next[idx] = prev[ki]
        else next[ki] = prev[ki]
      }
      return next
    })
  }

  const updateAnswer = (idx: number, answer: string) => {
    const updated = finalQuestions.map((q, i) => i === idx ? { ...q, expected_answer: answer } : q)
    setFinalQuestions(updated)
  }

  const handleGenerateAnswer = async (idx: number) => {
    const q = finalQuestions[idx]
    if (!q) return
    setAnswerGenerating((prev) => ({ ...prev, [idx]: true }))
    setAnswerExpanded((prev) => ({ ...prev, [idx]: true }))
    try {
      const res = await interviewerApi.generateAnswerForQuestion({ question: q.text, client_user_id: clientUserId })
      updateAnswer(idx, res.expected_answer)
    } catch {
      // keep existing answer if any
    } finally {
      setAnswerGenerating((prev) => ({ ...prev, [idx]: false }))
    }
  }

  const missingType = finalQuestions.some((q) => !q.question_type_id)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* AI Generator */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={14} style={{ color: '#7c3aed' }} /> AI-Generated Questions
          </div>
          <button className="btn btn-outline" onClick={handleGenerate} disabled={generating} style={{ fontSize: 12, padding: '5px 12px' }}>
            {generating
              ? <><RotateCcw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
              : <><Sparkles size={13} /> Generate</>}
          </button>
        </div>
        {genError && (
          <div style={{ display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 10 }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {genError}
          </div>
        )}
        {generatedQuestions.length > 0 && (
          <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
            {generatedQuestions.map((q, i) => {
              const inFinal = finalQuestions.some((fq) => fq.text === q.text)
              const selectedType = aiQTypes[i]
              const typeNotSelected = !selectedType
              return (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'white' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                      <p style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.5, margin: 0, fontWeight: 500 }}>{q.text}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                      {questionTypes.length > 0 && !inFinal && (
                        <select
                          id={`ai-qtype-${i}`}
                          className="form-input"
                          value={selectedType ?? ''}
                          onChange={(e) => setAiQTypes((prev) => ({ ...prev, [i]: e.target.value ? parseInt(e.target.value) : '' }))}
                          style={{ fontSize: 12, padding: '4px 8px', width: 'auto', minWidth: 140, borderColor: typeNotSelected ? '#fca5a5' : undefined }}
                          title="Select question type (required)"
                        >
                          <option value="">Select type *</option>
                          {questionTypes.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      )}
                      <button
                        className="btn btn-outline"
                        style={{ padding: '4px 10px', fontSize: 12, whiteSpace: 'nowrap', ...(inFinal || typeNotSelected ? { opacity: 0.4 } : {}) }}
                        onClick={() => addToFinal(q, i)}
                        disabled={inFinal || typeNotSelected}
                        title={typeNotSelected && !inFinal ? 'Select a question type first' : undefined}
                      >
                        <Plus size={13} /> Add
                      </button>
                    </div>
                  </div>
                  {q.expected_answer && (
                    <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(124,58,237,0.05)', borderRadius: 8, borderLeft: '3px solid #7c3aed' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                        Expected Answer
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--text-dark)', lineHeight: 1.6, margin: 0 }}>{q.expected_answer}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Final Question Set builder */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', background: '#fafbfc', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ListChecks size={15} style={{ color: 'var(--primary-navy)' }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-dark)', flex: 1 }}>
            Final Question Set {finalQuestions.length > 0 && <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>({finalQuestions.length} questions)</span>}
          </span>
          {existingQS?.is_finalized && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: '#dcfce7', color: '#16a34a' }}>Saved</span>
          )}
        </div>
        <div style={{ padding: 16 }}>
          {/* Custom input row */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Type a custom question and press Add…"
              value={customQuestion}
              onChange={(e) => setCustomQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom() } }}
              style={{ flex: '1 1 180px', minWidth: 0, fontSize: 13 }}
            />
            {questionTypes.length > 0 && (
              <select
                id="custom-qtype-select"
                className="form-input"
                value={customQTypeId}
                onChange={(e) => setCustomQTypeId(e.target.value ? parseInt(e.target.value) : '')}
                style={{ fontSize: 13, padding: '6px 10px', width: 'auto', minWidth: 150, borderColor: customQuestion.trim() && !customQTypeId ? '#fca5a5' : undefined }}
                title="Question type (required)"
              >
                <option value="">Question type *</option>
                {questionTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
            <button
              className="btn btn-outline"
              onClick={addCustom}
              disabled={!customQuestion.trim() || (questionTypes.length > 0 && !customQTypeId)}
              style={{ fontSize: 13, padding: '6px 14px', flexShrink: 0, ...(!customQuestion.trim() || (questionTypes.length > 0 && !customQTypeId) ? { opacity: 0.4 } : {}) }}
              title={customQuestion.trim() && !customQTypeId ? 'Select a question type first' : undefined}
            >
              <Plus size={14} /> Add
            </button>
          </div>

          {finalQuestions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {finalQuestions.map((q, i) => (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fafbfc', overflow: 'hidden' }}>
                  {/* Question row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 20, paddingTop: 2 }}>{i + 1}.</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.5, margin: '0 0 4px' }}>{q.text}</p>
                      {q.question_type_name ? (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: '#e0f2fe', color: '#0369a1', display: 'inline-block' }}>
                          {q.question_type_name}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <AlertCircle size={11} /> No type selected
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {/* Reorder buttons */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <button
                          title="Move up"
                          onClick={() => moveQuestion(i, -1)}
                          disabled={i === 0}
                          style={{ background: 'none', border: 'none', cursor: i === 0 ? 'not-allowed' : 'pointer', color: 'var(--text-muted)', padding: 0, opacity: i === 0 ? 0.3 : 1, lineHeight: 0 }}
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          title="Move down"
                          onClick={() => moveQuestion(i, 1)}
                          disabled={i === finalQuestions.length - 1}
                          style={{ background: 'none', border: 'none', cursor: i === finalQuestions.length - 1 ? 'not-allowed' : 'pointer', color: 'var(--text-muted)', padding: 0, opacity: i === finalQuestions.length - 1 ? 0.3 : 1, lineHeight: 0 }}
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>
                      {/* Suggest Answer button — shown for all questions */}
                      <button
                        title={q.expected_answer ? 'Regenerate expected answer' : 'Suggest expected answer'}
                        onClick={() => handleGenerateAnswer(i)}
                        disabled={answerGenerating[i]}
                        style={{
                          background: 'none', border: '1px solid #7c3aed', borderRadius: 6, cursor: 'pointer',
                          color: '#7c3aed', fontSize: 11, fontWeight: 600, padding: '3px 8px',
                          display: 'flex', alignItems: 'center', gap: 4,
                          opacity: answerGenerating[i] ? 0.6 : 1,
                        }}
                      >
                        {answerGenerating[i]
                          ? <><RotateCcw size={11} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
                          : <><Sparkles size={11} /> {q.expected_answer ? 'Re-suggest Answer' : 'Suggest Answer'}</>}
                      </button>
                      {/* Toggle answer visibility if answer exists */}
                      {q.expected_answer && (
                        <button
                          title={answerExpanded[i] ? 'Hide answer' : 'Show answer'}
                          onClick={() => setAnswerExpanded((prev) => ({ ...prev, [i]: !prev[i] }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 4px' }}
                        >
                          {answerExpanded[i] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      )}
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '2px 4px' }} onClick={() => remove(i)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  {/* Expected answer area — shown when expanded or just generated */}
                  {(answerExpanded[i] || answerGenerating[i]) && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '10px 12px', background: 'white' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Sparkles size={11} /> Expected Answer <span style={{ fontWeight: 400, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0 }}>(optional · editable)</span>
                      </div>
                      <textarea
                        className="form-input"
                        rows={3}
                        placeholder={answerGenerating[i] ? 'Generating answer…' : 'Write or edit the expected answer…'}
                        value={q.expected_answer ?? ''}
                        onChange={(e) => updateAnswer(i, e.target.value)}
                        disabled={answerGenerating[i]}
                        style={{ fontSize: 12, lineHeight: 1.6, resize: 'vertical', minHeight: 72 }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {missingType && finalQuestions.length > 0 && (
            <div style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 10, background: 'rgba(220,38,38,0.05)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.2)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={13} /> Some questions are missing a question type. Please remove and re-add them with a type selected.
            </div>
          )}

          {saveMsg && (
            <div style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 10, background: saveMsg.includes('Failed') ? 'rgba(220,38,38,0.06)' : '#f0fdf4', color: saveMsg.includes('Failed') ? '#dc2626' : '#16a34a', border: `1px solid ${saveMsg.includes('Failed') ? 'rgba(220,38,38,0.2)' : '#86efac'}` }}>
              {saveMsg}
            </div>
          )}

          {finalQuestions.length > 0 && (
            <div className="ic-qs-save-row">
              <button className="btn btn-outline" onClick={() => setFinalQuestions([])} style={{ fontSize: 13 }}>
                <Trash2 size={13} /> Clear
              </button>
              <button
                className="btn btn-primary"
                onClick={onSave}
                disabled={saving}
                style={{ fontSize: 13 }}
              >
                <Send size={13} />
                {saving ? 'Saving…' : `Save Question Set (${finalQuestions.length})`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Client Profile Panel ───────────────────────────────────────────────────────

function ClientProfile({ client, onClose, interviewerTz, targetBookingId }: { client: Client; onClose: () => void; interviewerTz: string | undefined; targetBookingId?: number | null }) {
  const [sessions, setSessions] = useState<SessionItem[]>([])
  const [loadingSessions, setLoadingSessions] = useState(true)
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([])
  const [loadingQS, setLoadingQS] = useState(true)

  // Prep info from latest booking (takes priority over standing client profile values)
  const [jobDescriptionText, setJobDescriptionText] = useState<string | null>(null)
  const [jobDescriptionPath, setJobDescriptionPath] = useState<string | null>(null)
  const [jdDownloadUrl, setJdDownloadUrl] = useState<string | null>(null)
  const [jdDownloading, setJdDownloading] = useState(false)
  const [specialNotes, setSpecialNotes] = useState<string | null>(null)
  // Booking-specific overrides for target role and interview goals
  const [prepTargetRole, setPrepTargetRole] = useState<string | null>(null)
  const [prepInterviewGoals, setPrepInterviewGoals] = useState<string | null>(null)

  // Which upcoming session prep panel is open
  const [activePrepId, setActivePrepId] = useState<number | null>(null)

  // Per-session question builder state: sessionId → QuestionItem[]
  const [sessionFinalQs, setSessionFinalQs] = useState<Record<number, QuestionItem[]>>({})
  const [sessionSaving, setSessionSaving] = useState<Record<number, boolean>>({})
  const [sessionSaveMsg, setSessionSaveMsg] = useState<Record<number, string>>({})

  // Which saved QS row is expanded in "Currently Saved" panel (prep): qs.id
  const [expandedQsId, setExpandedQsId] = useState<number | null>(null)
  // Which question set is being deleted (shows spinner)
  const [deletingQsId, setDeletingQsId] = useState<number | null>(null)

  // Which target-role group is expanded in "Previously Saved" standalone card
  const [expandedPrevRole, setExpandedPrevRole] = useState<string | null>(null)

  // Shared AI generated questions (for the active prep panel)
  const [generatedQuestions, setGeneratedQuestions] = useState<QuestionItem[]>([])
  const [generating, setGenerating] = useState(false)

  // Completed sessions: expand + notes + recordings
  const [expandedCompletedId, setExpandedCompletedId] = useState<number | null>(null)
  const [sessionNotes, setSessionNotes] = useState<Record<number, Note[]>>({})
  const [recordings, setRecordings] = useState<RecordingMeta[]>([])
  const [playingRec, setPlayingRec] = useState<RecordingMeta | null>(null)
  const [transcriptRec, setTranscriptRec] = useState<RecordingMeta | null>(null)
  const [transcriptCues, setTranscriptCues] = useState<{ time: string; speaker: string; text: string }[]>([])
  const [transcriptLoading, setTranscriptLoading] = useState(false)
  const [transcriptError, setTranscriptError] = useState('')

  const openTranscript = async (rec: RecordingMeta) => {
    setTranscriptRec(rec)
    setTranscriptCues([])
    setTranscriptError('')
    setTranscriptLoading(true)
    try {
      const vttText = await interviewerApi.getRecordingTranscript(rec.id)
      const cues = parseVtt(vttText)
      if (cues.length === 0) setTranscriptError('No transcript content available.')
      else setTranscriptCues(cues)
    } catch {
      setTranscriptError('Transcript is not available for this session.')
    } finally {
      setTranscriptLoading(false)
    }
  }

  // Suggested question templates
  const [suggestedTemplates, setSuggestedTemplates] = useState<QuestionTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<QuestionTemplate | null>(null)

  // Question types for the question builder dropdown
  const [questionTypes, setQuestionTypes] = useState<QuestionType[]>([])

  const displayName = client.full_name || client.email || 'Client'
  const autoOpenDone = useRef(false)
  const sessionCardRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const [highlightedSessionId, setHighlightedSessionId] = useState<number | null>(null)

  // ── Load everything on mount ────────────────────────────────────────────────

  useEffect(() => {
    autoOpenDone.current = false
    setLoadingSessions(true)
    setLoadingQS(true)

    // Load question types for the question builder dropdown
    interviewerApi.getQuestionTypes().catch(() => []).then((types: QuestionType[]) => {
      setQuestionTypes(Array.isArray(types) ? types : [])
    })

    Promise.all([
      interviewerApi.getClientSessions(client.user_id).catch(() => []),
      interviewerApi.getClientQuestionSets(client.user_id).catch(() => []),
      interviewerApi.getClientPrepInfo(client.user_id, targetBookingId ?? undefined).catch(() => null),
      interviewerApi.getRecordings().catch(() => []),
    ]).then(([sessData, qsData, prepData, recData]) => {
      const sess: SessionItem[] = Array.isArray(sessData) ? sessData : []
      const qs: QuestionSet[] = Array.isArray(qsData) ? qsData : []
      setSessions(sess)
      setQuestionSets(qs)
      setRecordings(Array.isArray(recData) ? recData : [])
      setLoadingSessions(false)
      setLoadingQS(false)

      if (prepData) {
        setJobDescriptionText(prepData.job_description_text ?? null)
        setJobDescriptionPath(prepData.job_description_path ?? null)
        setSpecialNotes(prepData.special_notes ?? null)
        setPrepTargetRole(prepData.target_role ?? null)
        setPrepInterviewGoals(prepData.interview_goals ?? null)
      }

      // Fetch templates after prepData is loaded so session-specific target_role takes priority
      const effectiveRole = (prepData?.target_role || client.target_role) ?? undefined
      interviewerApi.getQuestionTemplates(effectiveRole).catch(() => []).then((templates: QuestionTemplate[]) => {
        setSuggestedTemplates(templates)
      })

      // Auto-open the targeted session (from dashboard Prep button) or fall back to first upcoming
      const upcomings = sess.filter((s) => s.status !== 'completed')
      if (upcomings.length > 0 && !autoOpenDone.current) {
        autoOpenDone.current = true
        const target = targetBookingId
          ? (upcomings.find((s) => s.booking_id === targetBookingId) ?? upcomings[0])
          : upcomings[0]
        setActivePrepId(target.id)
        const existingQS = qs.find((q) => q.session_id === target.id)
        if (existingQS && !existingQS.is_finalized) {
          setSessionFinalQs((prev) => ({ ...prev, [target.id]: existingQS.questions }))
        }
        // Scroll into view and highlight when coming from dashboard
        if (targetBookingId) {
          setHighlightedSessionId(target.id)
          setTimeout(() => {
            sessionCardRefs.current[target.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }, 100)
          setTimeout(() => setHighlightedSessionId(null), 2000)
        }
      }
    })
  }, [client.user_id])

  // ── Derived ─────────────────────────────────────────────────────────────────

  const upcoming = sessions.filter((s) => s.status !== 'completed').sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())
  const completed = sessions.filter((s) => s.status === 'completed')
  const getQS = (sessionId: number) => questionSets.find((q) => q.session_id === sessionId) ?? null
  const getSessionBundles = (sessionId: number) => questionSets.filter((q) => q.session_id === sessionId)
  const getFinalQs = (sessionId: number) => sessionFinalQs[sessionId] ?? []

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const togglePrepPanel = (sessionId: number) => {
    if (activePrepId === sessionId) {
      setActivePrepId(null)
      return
    }
    setActivePrepId(sessionId)
    setGeneratedQuestions([])
    // Only pre-populate builder if QS is a draft (not yet finalized/saved)
    const existing = getQS(sessionId)
    if (existing && !existing.is_finalized && !sessionFinalQs[sessionId]) {
      setSessionFinalQs((prev) => ({ ...prev, [sessionId]: existing.questions }))
    }
  }

  const setFinalQsForSession = (sessionId: number, qs: QuestionItem[]) => {
    setSessionFinalQs((prev) => ({ ...prev, [sessionId]: qs }))
  }

  const saveQS = async (sessionId: number) => {
    const questions = getFinalQs(sessionId)
    if (!questions.length) return
    // Enforce question type on every question
    const missing = questions.filter((q) => !q.question_type_id)
    if (missing.length > 0) {
      setSessionSaveMsg((prev) => ({ ...prev, [sessionId]: 'All questions must have a question type selected.' }))
      setTimeout(() => setSessionSaveMsg((prev) => ({ ...prev, [sessionId]: '' })), 4000)
      return
    }
    setSessionSaving((prev) => ({ ...prev, [sessionId]: true }))
    try {
      await interviewerApi.saveQuestionSet({ session_id: sessionId, questions, is_finalized: true })
      const updated: QuestionSet[] = await interviewerApi.getClientQuestionSets(client.user_id).catch(() => questionSets)
      setQuestionSets(Array.isArray(updated) ? updated : questionSets)
      // Clear the builder so the saved set moves cleanly to "Previously Saved"
      setSessionFinalQs((prev) => ({ ...prev, [sessionId]: [] }))
      setSessionSaveMsg((prev) => ({ ...prev, [sessionId]: 'Question set saved successfully!' }))
      setTimeout(() => setSessionSaveMsg((prev) => ({ ...prev, [sessionId]: '' })), 4000)
    } catch {
      setSessionSaveMsg((prev) => ({ ...prev, [sessionId]: 'Failed to save. Please try again.' }))
    }
    setSessionSaving((prev) => ({ ...prev, [sessionId]: false }))
  }

  const handleDeleteQS = async (qsId: number) => {
    if (deletingQsId !== null) return
    setDeletingQsId(qsId)
    try {
      await interviewerApi.deleteQuestionSet(qsId)
      const updated: QuestionSet[] = await interviewerApi.getClientQuestionSets(client.user_id).catch(() => questionSets)
      setQuestionSets(Array.isArray(updated) ? updated : questionSets.filter((q) => q.id !== qsId))
      if (expandedQsId === qsId) setExpandedQsId(null)
    } catch {
      // silently ignore — keep list as-is
    }
    setDeletingQsId(null)
  }

  const handleExpandCompleted = (sessionId: number) => {
    if (expandedCompletedId === sessionId) { setExpandedCompletedId(null); return }
    setExpandedCompletedId(sessionId)
    if (!(sessionId in sessionNotes)) {
      evaluationsApi.getNotes(sessionId)
        .then((data) => {
          const notes: Note[] = Array.isArray(data) ? data : (data?.data ?? [])
          setSessionNotes((prev) => ({ ...prev, [sessionId]: notes }))
        })
        .catch(() => setSessionNotes((prev) => ({ ...prev, [sessionId]: [] })))
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Client Header ── */}
      <div className="ic-client-hdr">
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--primary-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, flexShrink: 0 }}>
          {displayName.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-dark)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Briefcase size={13} /> {client.target_role || 'No target role set'}
            </span>
            {client.email && (
              <a href={`mailto:${client.email}`} style={{ fontSize: 13, color: 'var(--primary-navy)', display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                <Mail size={13} style={{ flexShrink: 0 }} /> {client.email}
              </a>
            )}
            {client.experience_level && (
              <span className="badge badge-primary" style={{ fontSize: 12 }}>{client.experience_level}</span>
            )}
          </div>
        </div>
        <div className="ic-client-stats">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary-navy)' }}>
              {loadingSessions ? '—' : upcoming.length}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Upcoming</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary-navy)' }}>
              {loadingSessions ? '—' : completed.length}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Completed</div>
          </div>
          {client.linkedin_url && (
            <a href={client.linkedin_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: 13 }}>
              <Linkedin size={14} /> LinkedIn
            </a>
          )}
          <button className="btn btn-outline" style={{ padding: '8px 14px', fontSize: 13 }} onClick={onClose}>
            ← Back
          </button>
        </div>
      </div>

      {/* ── Upcoming Interviews ── */}
      {loadingSessions ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
          <RotateCcw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
          <p style={{ fontSize: 13 }}>Loading sessions…</p>
        </div>
      ) : upcoming.length > 0 ? (
        <div style={{ marginBottom: 24 }}>
          <div className="ic-upcoming-hdr">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Calendar size={16} style={{ color: 'var(--primary-navy)', flexShrink: 0 }} />
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>Upcoming Interviews ({upcoming.length})</h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(245,158,11,0.1)', borderRadius: 20 }}>
              <Info size={12} style={{ color: '#d97706', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#d97706', fontWeight: 600 }}>Create a Final Question Set before joining each interview</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {upcoming.map((session) => {
              const { date, time } = formatDateTime(session.scheduled_at, interviewerTz)
              const daysUntil = getDaysUntil(session.scheduled_at)
              const savedQS = getQS(session.id)
              const hasQS = savedQS?.is_finalized === true
              const isOpen = activePrepId === session.id
              const finalQs = getFinalQs(session.id)

              const isHighlighted = highlightedSessionId === session.id
              return (
                <div
                  key={session.id}
                  ref={(el) => { sessionCardRefs.current[session.id] = el }}
                  style={{
                    border: `2px solid ${isHighlighted ? '#6366f1' : hasQS ? '#86efac' : 'var(--border)'}`,
                    borderRadius: 14,
                    overflow: 'hidden',
                    boxShadow: isHighlighted ? '0 0 0 4px rgba(99,102,241,0.15), 0 4px 16px rgba(0,0,0,0.07)' : isOpen ? '0 4px 16px rgba(0,0,0,0.07)' : 'none',
                    transition: 'border-color 0.4s, box-shadow 0.4s',
                  }}
                >

                  {/* Session header */}
                  <div style={{ padding: '16px 20px', background: hasQS ? '#f0fdf4' : 'white' }}>
                    <div className="ic-session-row">

                      {/* Status dot */}
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: hasQS ? '#dcfce7' : 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {hasQS
                          ? <CheckCircle size={22} style={{ color: '#16a34a' }} />
                          : <Clock size={22} style={{ color: '#d97706' }} />}
                      </div>

                      {/* Info */}
                      <div className="ic-session-info">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                            <Calendar size={12} /> {date}
                          </span>
                          <span style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                            <Clock size={12} /> {time}
                          </span>
                          <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{session.duration_minutes} min</span>
                          {daysUntil === 0 && <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: '#fef3c7', color: '#d97706', whiteSpace: 'nowrap' }}>Today</span>}
                          {daysUntil === 1 && <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: '#fef3c7', color: '#d97706', whiteSpace: 'nowrap' }}>Tomorrow</span>}
                          {daysUntil > 1 && daysUntil <= 7 && <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: 'rgba(59,130,246,0.1)', color: '#2563eb', whiteSpace: 'nowrap' }}>in {daysUntil} days</span>}
                        </div>
                      </div>

                      {/* QS status badge + Prepare button — grouped so they move together on mobile */}
                      <div className="ic-session-actions">
                        {hasQS ? (
                          <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                            <CheckCircle size={12} /> Question Set Ready
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                            <TriangleAlert size={12} /> Needs Preparation
                          </span>
                        )}
                        <button
                          className="btn btn-outline"
                          onClick={() => togglePrepPanel(session.id)}
                          style={{ fontSize: 13, padding: '7px 14px' }}
                        >
                          {isOpen ? <><ChevronUp size={14} /> Close Prep</> : <><ListChecks size={14} /> Prepare</>}
                        </button>
                      </div>
                    </div>

                    {/* Prep reminder message — always visible */}
                    <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: hasQS ? '#f0fdf4' : '#fef9ec', border: `1px solid ${hasQS ? '#86efac' : '#fde68a'}`, borderRadius: 8 }}>
                      {hasQS
                        ? <CheckCircle size={14} style={{ color: '#16a34a', flexShrink: 0 }} />
                        : <Info size={14} style={{ color: '#d97706', flexShrink: 0 }} />}
                      <span style={{ fontSize: 12, color: hasQS ? '#15803d' : '#92400e' }}>
                        {hasQS
                          ? <><strong>Final Question Set saved.</strong> You are ready for this interview. Please join from the Sessions page when it's time.</>
                          : <>Please <strong>Prep and Create a Final Question Set</strong> before joining each interview. {!isOpen && <>Click <strong>Prepare</strong> to get started.</>}</>}
                      </span>
                    </div>
                  </div>

                  {/* ── Prep Panel (expanded) ── */}
                  {isOpen && (
                    <div style={{ borderTop: '1px solid var(--border)', background: '#fafbfc' }}>

                      {/* 2-column layout: client info | question builder */}
                      <div className="ic-prep-grid">

                        {/* Left: Client context */}
                        <div style={{ borderRight: '1px solid var(--border)', padding: 20, display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Client Context</div>

                          {/* Client info */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 14 }}>
                            <InfoRow label="Target Role">{prepTargetRole || client.target_role || '—'}</InfoRow>
                            <InfoRow label="Experience">{client.experience_level || '—'}</InfoRow>
                            <InfoRow label="Timezone">{client.timezone ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Globe size={12} />{client.timezone}</span> : '—'}</InfoRow>
                            <InfoRow label="Language">{client.language ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Languages size={12} />{client.language}</span> : '—'}</InfoRow>
                            <InfoRow label="LinkedIn">
                              {client.linkedin_url
                                ? <a href={client.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#0a66c2', fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>
                                    <Linkedin size={13} /> View Profile
                                  </a>
                                : '—'}
                            </InfoRow>
                          </div>

                          {/* Interview Goals */}
                          {(prepInterviewGoals || client.interview_goals) && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>Interview Goals</div>
                              <div style={{ maxHeight: 100, overflowY: 'auto', padding: '6px 10px', background: 'white', border: '1px solid var(--border)', borderRadius: 6 }}>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                  {(prepInterviewGoals || client.interview_goals || '').split(';').map((goal, i) => goal.trim() && (
                                    <li key={i} style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.6 }}>{goal.trim()}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* Special Notes */}
                          {specialNotes && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>Special Notes</div>
                              <div style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.6, maxHeight: 80, overflowY: 'auto', padding: '8px 10px', background: 'white', border: '1px solid var(--border)', borderRadius: 6, whiteSpace: 'pre-wrap' }}>{specialNotes}</div>
                            </div>
                          )}

                          {/* Resume */}
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>Resume</div>
                            {client.resume_url ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 8 }}>
                                <FileText size={16} color="#16a34a" />
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#15803d', flex: 1 }}>Resume on file</span>
                                <a href={client.resume_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ fontSize: 12, padding: '4px 10px' }}>
                                  <FileText size={12} /> View
                                </a>
                              </div>
                            ) : (
                              <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No resume uploaded</p>
                            )}
                          </div>

                          {/* Job Description */}
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>Job Description</div>
                            {jobDescriptionPath && (
                              <div style={{ marginBottom: 8 }}>
                                {jdDownloadUrl ? (
                                  <a href={jdDownloadUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ fontSize: 12, padding: '4px 10px' }}>
                                    <FileText size={12} /> View JD (PDF)
                                  </a>
                                ) : (
                                  <button
                                    className="btn btn-outline"
                                    style={{ fontSize: 12, padding: '4px 10px' }}
                                    disabled={jdDownloading}
                                    onClick={async () => {
                                      setJdDownloading(true)
                                      try {
                                        const res = await interviewerApi.getJdSignedUrl(jobDescriptionPath)
                                        setJdDownloadUrl(res.signed_url)
                                        window.open(res.signed_url, '_blank')
                                      } catch { alert('Failed to generate link.') } finally { setJdDownloading(false) }
                                    }}
                                  >
                                    <FileText size={12} /> {jdDownloading ? 'Loading…' : 'View JD (PDF)'}
                                  </button>
                                )}
                              </div>
                            )}
                            {jobDescriptionText ? (
                              <div style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.6, maxHeight: 320, overflowY: 'auto', padding: '8px 10px', background: 'white', border: '1px solid var(--border)', borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                                {jobDescriptionText}
                              </div>
                            ) : !jobDescriptionPath ? (
                              <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No job description provided</p>
                            ) : null}
                          </div>

                          {/* ── Suggested Question Templates ── */}
                          <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                                <Layers size={11} /> Suggested Question Templates
                              </div>
                              {suggestedTemplates.length === 0 ? (
                                <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No templates found for this role.</p>
                              ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {suggestedTemplates.map((tmpl) => (
                                  <button
                                    key={tmpl.id}
                                    type="button"
                                    onClick={() => setSelectedTemplate(tmpl)}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 10,
                                      padding: '10px 12px', background: 'white',
                                      border: '1px solid var(--border)', borderRadius: 8,
                                      cursor: 'pointer', textAlign: 'left', width: '100%',
                                      transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary-navy)'; e.currentTarget.style.background = '#f0f4ff' }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white' }}
                                  >
                                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(45,55,72,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                      <Layers size={15} style={{ color: 'var(--primary-navy)' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tmpl.title}</div>
                                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                                        {tmpl.questions.length} question{tmpl.questions.length !== 1 ? 's' : ''}
                                        {tmpl.tier && <> · <span style={{ textTransform: 'capitalize' }}>{tmpl.tier}</span></>}
                                      </div>
                                    </div>
                                    <Eye size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                                  </button>
                                ))}
                              </div>
                              )}
                            </div>
                        </div>

                        {/* Right: Build Final Question Set + Currently Saved Question Sets */}
                        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0, overflow: 'hidden' }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 16 }}>
                              Build Final Question Set for this Interview
                            </div>
                            <QuestionSetBuilder
                              sessionId={session.id}
                              finalQuestions={finalQs}
                              setFinalQuestions={(qs) => setFinalQsForSession(session.id, qs)}
                              generatedQuestions={generatedQuestions}
                              setGeneratedQuestions={setGeneratedQuestions}
                              generating={generating}
                              setGenerating={setGenerating}
                              saving={sessionSaving[session.id] ?? false}
                              saveMsg={sessionSaveMsg[session.id] ?? ''}
                              onSave={() => saveQS(session.id)}
                              clientUserId={client.user_id}
                              existingQS={savedQS}
                              questionTypes={questionTypes}
                              jobDescription={jobDescriptionText}
                            />
                          </div>

                          {/* Currently Saved Question Sets — same column width as builder */}
                          {(() => {
                            const rawBundles = getSessionBundles(session.id)
                            if (rawBundles.length === 0) return null
                            // Sort ascending by created_at: oldest = index 0 = "Question Set 1", newest = last = "Latest"
                            const sessionBundles = [...rawBundles].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                            return (
                              <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                                {/* Card header */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', background: '#fafbfc' }}>
                                  <ListChecks size={15} style={{ color: 'var(--primary-navy)' }} />
                                  <div style={{ flex: 1 }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-dark)' }}>Currently Saved Question Sets</span>
                                  </div>
                                  <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: 'var(--primary-navy)', color: 'white' }}>
                                    {sessionBundles.reduce((t, b) => t + b.questions.length, 0)} questions
                                  </span>
                                </div>

                                {/* Bundle rows */}
                                {sessionBundles.map((bundle, bi) => {
                                  const isExp = expandedQsId === bundle.id
                                  const isDeleting = deletingQsId === bundle.id
                                  const isLatest = bi === sessionBundles.length - 1
                                  return (
                                    <div key={bundle.id} style={{ borderTop: bi > 0 ? '1px solid var(--border)' : 'none' }}>
                                      <div
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', cursor: 'pointer', background: isExp ? '#f8f9fa' : 'white' }}
                                        onClick={() => setExpandedQsId(isExp ? null : bundle.id)}
                                      >
                                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 22 }}>#{bi + 1}</span>
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)' }}>Question Set {bi + 1}</span>
                                          {isLatest && (
                                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: '#16a34a', color: 'white' }}>Latest</span>
                                          )}
                                        </div>
                                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatShortDate(bundle.created_at, interviewerTz)}</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dark)', minWidth: 70, textAlign: 'right' }}>{bundle.questions.length} questions</span>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDeleteQS(bundle.id) }}
                                          disabled={isDeleting || deletingQsId !== null}
                                          style={{ background: 'none', border: 'none', cursor: isDeleting || deletingQsId !== null ? 'not-allowed' : 'pointer', color: '#dc2626', opacity: isDeleting ? 0.5 : 1, padding: '2px 4px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                                          title="Delete this question set"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                        <ChevronDown size={15} style={{ color: 'var(--text-muted)', transform: isExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                                      </div>

                                      {isExp && (
                                        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px', background: '#fafbfc', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                          {bundle.questions.map((q, qi) => (
                                            <div key={qi} style={{ display: 'flex', gap: 10, padding: '9px 12px', background: 'white', border: '1px solid var(--border)', borderRadius: 8 }}>
                                              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 20, paddingTop: 1, flexShrink: 0 }}>{qi + 1}.</span>
                                              <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.5, margin: '0 0 5px' }}>{q.text}</p>
                                                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                                  {q.question_type_name && (
                                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: '#e0f2fe', color: '#0369a1' }}>
                                                      {q.question_type_name}
                                                    </span>
                                                  )}
                                                  {q.source === 'ai' && (
                                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: 'rgba(139,92,246,0.1)', color: '#7c3aed' }}>AI</span>
                                                  )}
                                                </div>
                                                {q.expected_answer && (
                                                  <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(139,92,246,0.06)', borderLeft: '3px solid #7c3aed', borderRadius: 6 }}>
                                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Expected Answer</div>
                                                    <p style={{ fontSize: 12, color: 'var(--text-dark)', lineHeight: 1.55, margin: 0, whiteSpace: 'pre-wrap' }}>{q.expected_answer}</p>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )
                          })()}
                        </div>
                      </div>

                      {/* Question Type Guide (below) */}
                      <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px' }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Info size={13} /> Question Type Guide
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
                          {[
                            { icon: <MessageSquare size={16} />, iconBg: 'rgba(59,130,246,0.1)', iconColor: 'rgb(59,130,246)', title: 'Behavioral', items: ['Past experience', 'Real example', 'Actions taken'], example: '“Tell me about a time…”' },
                            { icon: <HelpCircle size={16} />, iconBg: 'rgba(16,185,129,0.1)', iconColor: 'rgb(16,185,129)', title: 'Situational', items: ['Hypothetical scenario', 'Decision-making', 'Problem-solving approach'], example: '“What would you do if…”' },
                            { icon: <Heart size={16} />, iconBg: 'rgba(236,72,153,0.1)', iconColor: 'rgb(236,72,153)', title: 'Motivation', items: ['Why this role', 'Why this company', 'Career goals'], example: '“Why are you interested…”' },
                            { icon: <Briefcase size={16} />, iconBg: 'rgba(245,158,11,0.1)', iconColor: 'rgb(245,158,11)', title: 'Role-Specific', items: ['Job-related skills', 'Basic knowledge', 'Approach to tasks'], example: '“How would you handle…”' },
                            { icon: <FileText size={16} />, iconBg: 'rgba(139,92,246,0.1)', iconColor: 'rgb(139,92,246)', title: 'Resume-Based', items: ['Clarify experience', 'Verify ownership', 'Depth of involvement'], example: '“Walk me through…”' },
                          ].map((card) => (
                            <div key={card.title} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', display: 'flex', gap: 10, background: 'white' }}>
                              <div style={{ width: 30, height: 30, borderRadius: 8, background: card.iconBg, color: card.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{card.icon}</div>
                              <div style={{ minWidth: 0 }}>
                                <h5 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>{card.title}</h5>
                                <ul style={{ margin: '0 0 6px', paddingLeft: 14, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                  {card.items.map((item) => (
                                    <li key={item} style={{ fontSize: 12, color: card.iconColor }}>{item}</li>
                                  ))}
                                </ul>
                                <div style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--text-muted)' }}>{card.example}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {/* ── Previously Saved Question Sets (from completed sessions) ── */}
      {(() => {
        if (loadingQS || loadingSessions) return null
        const completedIds = new Set(completed.map((s) => s.id))
        const completedBundles = questionSets.filter((q) => completedIds.has(q.session_id))

        // Group by target role (generation_context first, then client profile, then session title)
        const groups: Record<string, { bundles: QuestionSet[]; sessionTitle: string; interviewerAlias: string | null }> = {}
        for (const bundle of completedBundles) {
          const sess = sessions.find((s) => s.id === bundle.session_id)
          const role =
            bundle.generation_context?.target_role ||
            client.target_role ||
            sess?.title ||
            'General Interview'
          if (!groups[role]) {
            groups[role] = {
              bundles: [],
              sessionTitle: sess?.title || 'Interview Session',
              interviewerAlias: sess?.interviewer_alias ?? null,
            }
          }
          groups[role].bundles.push(bundle)
        }
        const groupEntries = Object.entries(groups)

        return (
          <div style={{ marginTop: 32, marginBottom: 32 }}>
            {/* Section heading */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <BookOpen size={16} style={{ color: 'var(--primary-navy)' }} />
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)' }}>Previously Saved Question Sets</h3>
            </div>

            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              {completedBundles.length === 0 ? (
                <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <BookOpen size={36} style={{ marginBottom: 12, opacity: 0.25 }} />
                  <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No question sets from completed sessions yet</p>
                  <p style={{ fontSize: 13 }}>Question sets saved for upcoming sessions will appear here once those sessions are completed.</p>
                </div>
              ) : groupEntries.map(([role, group], gi) => {
                const allQ = group.bundles.flatMap((b) => b.questions)
                const isExpanded = expandedPrevRole === role

                return (
                  <div key={role} style={{ borderTop: gi > 0 ? '1px solid var(--border)' : 'none' }}>
                    {/* Role row — clickable */}
                    <div
                      className="ic-prev-row"
                      style={{ background: isExpanded ? '#f0f4ff' : 'white', transition: 'background 0.15s' }}
                      onClick={() => setExpandedPrevRole(isExpanded ? null : role)}
                      onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = '#fafbfc' }}
                      onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'white' }}
                    >
                      {/* Role icon */}
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: isExpanded ? 'rgba(37,99,235,0.1)' : 'rgba(45,55,72,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Briefcase size={18} style={{ color: isExpanded ? '#2563eb' : 'var(--primary-navy)' }} />
                      </div>

                      {/* Role info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: isExpanded ? '#2563eb' : 'var(--text-dark)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {role}
                        </div>
                        <div className="ic-prev-meta">
                          {group.interviewerAlias && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                              <Users size={11} /> {group.interviewerAlias}
                            </span>
                          )}
                          <span style={{ whiteSpace: 'nowrap' }}>Last saved {formatShortDate(group.bundles[group.bundles.length - 1].created_at, interviewerTz)}</span>
                        </div>
                      </div>

                      {/* Badges */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'var(--primary-navy)', color: 'white', whiteSpace: 'nowrap' }}>
                          {allQ.length} q's
                        </span>
                        <ChevronDown size={16} style={{ color: 'var(--text-muted)', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                      </div>
                    </div>

                    {/* Expanded: all questions merged from all bundles */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid var(--border)', background: '#fafbfc', padding: '0 0 4px' }}>
                        {/* All questions list */}
                        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {allQ.map((q, qi) => (
                            <div key={qi} style={{ display: 'flex', gap: 12, padding: '12px 16px', background: 'white', border: '1px solid var(--border)', borderRadius: 10 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: 'white', background: 'var(--primary-navy)', width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{qi + 1}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.6, margin: '0 0 5px' }}>{q.text}</p>
                                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                  {q.question_type_name && (
                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#e0f2fe', color: '#0369a1' }}>
                                      {q.question_type_name}
                                    </span>
                                  )}
                                  {q.source === 'ai' && (
                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(139,92,246,0.1)', color: '#7c3aed' }}>AI</span>
                                  )}
                                </div>
                                {q.expected_answer && (
                                  <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(139,92,246,0.06)', borderLeft: '3px solid #7c3aed', borderRadius: 6 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Expected Answer</div>
                                    <p style={{ fontSize: 12, color: 'var(--text-dark)', lineHeight: 1.55, margin: 0, whiteSpace: 'pre-wrap' }}>{q.expected_answer}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* ── Past Interviews ── */}
      <div style={{ marginTop: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <CheckCircle size={16} style={{ color: '#16a34a' }} />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)' }}>
            Completed Sessions ({loadingSessions ? '…' : completed.length})
          </h3>
        </div>

        {loadingSessions ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
        ) : completed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', background: 'white', border: '1px solid var(--border)', borderRadius: 14 }}>
            <CheckCircle size={36} style={{ marginBottom: 10, opacity: 0.3 }} />
            <p style={{ fontSize: 13 }}>No completed sessions yet.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {completed.map((session, idx) => {
              const { date, time } = formatDateTime(session.scheduled_at, interviewerTz)
              const isExpanded = expandedCompletedId === session.id
              const allBundles = getSessionBundles(session.id)
              const allQuestions = allBundles.flatMap((b) => b.questions)

              return (
                <div key={session.id} style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'white' }}>

                  {/* Row header */}
                  <div
                    className="ic-comp-row"
                    style={{ background: isExpanded ? '#f8f9fa' : 'white' }}
                    onClick={() => handleExpandCompleted(session.id)}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(22,163,74,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>#{idx + 1}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session.title}</div>
                      <div className="ic-comp-meta">
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}><Calendar size={11} />{date}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}><Clock size={11} />{time}</span>
                        <span style={{ whiteSpace: 'nowrap' }}>{session.duration_minutes} min</span>
                        {session.interviewer_alias && <span style={{ whiteSpace: 'nowrap' }}>· {session.interviewer_alias}</span>}
                      </div>
                    </div>
                    <div className="ic-comp-badges">
                      <span className="badge badge-success" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>Completed</span>
                      {allQuestions.length > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: 'rgba(45,55,72,0.07)', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          <BookOpen size={11} /> {allQuestions.length} Q's
                        </span>
                      )}
                      {isExpanded ? <ChevronUp size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} /> : <ChevronDown size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid var(--border)', background: '#fafbfc' }}>

                      {/* Session Details */}
                      {(() => {
                        const rec = recordings.find((r) =>
                          (session.booking_id ? r.booking_id === session.booking_id : r.session_id === session.id)
                        )
                        const rows: { label: string; value: React.ReactNode }[] = [
                          { label: 'Title', value: session.title },
                          { label: 'Date', value: date },
                          { label: 'Time', value: time },
                          { label: 'Duration', value: `${session.duration_minutes} minutes` },
                          { label: 'Target Role', value: client.target_role || '—' },
                          { label: 'Interviewer', value: session.interviewer_alias ? `Coach ${session.interviewer_alias}` : '—' },
                          {
                            label: 'Recording',
                            value: rec?.recording_url
                              ? <button onClick={() => setPlayingRec({ ...rec, duration_seconds: null })} style={{ background: 'none', border: '1px solid var(--primary-navy)', color: 'var(--primary-navy)', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Film size={13} /> Play Recording</button>
                              : <span style={{ color: 'var(--text-muted)' }}>Not available</span>,
                          },
                          {
                            label: 'Transcript',
                            value: rec?.transcript_url
                              ? <button onClick={() => openTranscript(rec)} style={{ background: 'none', border: '1px solid #7c3aed', color: '#7c3aed', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}><MessageSquare size={13} /> View Transcript</button>
                              : <span style={{ color: 'var(--text-muted)' }}>Not available</span>,
                          },
                        ]
                        return (
                          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Calendar size={12} /> Session Details
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <tbody>
                                {rows.map((row) => (
                                  <tr key={row.label} style={{ borderBottom: '1px solid var(--border)' }}>
                                    <td style={{ padding: '10px 12px 10px 0', width: 130, fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                      {row.label}
                                    </td>
                                    <td style={{ padding: '10px 0', fontSize: 14, fontWeight: 500, color: 'var(--text-dark)', verticalAlign: 'middle' }}>
                                      {row.value}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      })()}

                      {/* Final Question Set used in this session */}
                      <div style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <BookOpen size={12} /> Final Question Set Used
                        </div>
                        {allQuestions.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{allQuestions.length} questions</div>
                            {allQuestions.map((q, qi) => (
                              <div key={qi} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: 'white', border: '1px solid var(--border)', borderRadius: 8 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 20, paddingTop: 1 }}>{qi + 1}</span>
                                <div style={{ flex: 1 }}>
                                  <p style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.5, margin: 0 }}>{q.text}</p>
                                  {q.source === 'ai' && (
                                    <div style={{ marginTop: 6 }}>
                                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(139,92,246,0.1)', color: '#7c3aed' }}>AI</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No question set was saved for this session.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* ── Recording Modal ── */}
      {playingRec && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setPlayingRec(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 740, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
          >
            <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Film size={18} style={{ color: 'var(--primary-navy)' }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)' }}>Session Recording</span>
              </div>
              <button onClick={() => setPlayingRec(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <video
                src={buildRecordingSrc(playingRec.recording_url) ?? undefined}
                controls
                preload="metadata"
                style={{ width: '100%', borderRadius: 10, background: '#000', maxHeight: 400, outline: 'none' }}
                onDurationChange={(e) => {
                  const d = Math.round((e.target as HTMLVideoElement).duration)
                  if (d && isFinite(d)) {
                    setPlayingRec((prev) => prev ? { ...prev, duration_seconds: d } : prev)
                  }
                }}
              />
              {playingRec.duration_seconds != null && (
                <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                  <Clock size={13} />
                  Duration: {Math.floor(playingRec.duration_seconds / 60)}m {String(playingRec.duration_seconds % 60).padStart(2, '0')}s
                </p>
              )}
            </div>
            <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPlayingRec(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transcript Modal ── */}
      {transcriptRec && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => { setTranscriptRec(null); setTranscriptCues([]); setTranscriptError('') }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
          >
            {/* Header */}
            <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <MessageSquare size={18} style={{ color: '#7c3aed' }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)' }}>Session Transcript</span>
              </div>
              <button onClick={() => { setTranscriptRec(null); setTranscriptCues([]); setTranscriptError('') }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)' }}>
                <X size={20} />
              </button>
            </div>
            {/* Body */}
            <div style={{ padding: '20px 24px', maxHeight: 500, overflowY: 'auto' }}>
              {transcriptLoading && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>Loading transcript…</div>
              )}
              {!transcriptLoading && transcriptError && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                  <MessageSquare size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
                  <p style={{ fontSize: 14 }}>{transcriptError}</p>
                </div>
              )}
              {!transcriptLoading && transcriptCues.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', borderRadius: 10 }}>
                  {transcriptCues.map((cue, i) => (
                    <div key={i} style={{ display: 'flex', gap: 14, padding: '12px 16px', borderBottom: i < transcriptCues.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#7c3aed', whiteSpace: 'nowrap', paddingTop: 2, minWidth: 42 }}>{cue.time}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                        {cue.speaker && (
                          <span style={{
                            fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                            padding: '2px 7px', borderRadius: 4, width: 'fit-content',
                            background: cue.speaker.toLowerCase() === 'interviewer' ? 'rgba(37,99,235,0.1)' : 'rgba(22,163,74,0.1)',
                            color: cue.speaker.toLowerCase() === 'interviewer' ? '#2563eb' : '#16a34a',
                          }}>{cue.speaker}</span>
                        )}
                        <p style={{ fontSize: 13, color: 'var(--text-dark)', margin: 0, lineHeight: 1.5 }}>{cue.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Footer */}
            <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setTranscriptRec(null); setTranscriptCues([]); setTranscriptError('') }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Template Detail Modal ── */}
      {selectedTemplate && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setSelectedTemplate(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
          >
            {/* Modal header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(45,55,72,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Layers size={20} style={{ color: 'var(--primary-navy)' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px', lineHeight: 1.3 }}>{selectedTemplate.title}</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {selectedTemplate.target_role && (
                    <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: 'rgba(37,99,235,0.1)', color: '#2563eb' }}>
                      {selectedTemplate.target_role}
                    </span>
                  )}
                  <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: 'rgba(22,163,74,0.1)', color: '#16a34a' }}>
                    {selectedTemplate.questions.length} question{selectedTemplate.questions.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedTemplate(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)', flexShrink: 0 }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {selectedTemplate.description && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 18 }}>{selectedTemplate.description}</p>
              )}

              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>
                Sample Questions
              </div>

              {selectedTemplate.questions.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No questions in this template yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selectedTemplate.questions.map((q, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 12, padding: '12px 14px', background: '#f8f9fb', borderRadius: 10, border: '1px solid var(--border)' }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--primary-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
                        {idx + 1}
                      </div>
                      <p style={{ flex: 1, fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.55, margin: 0 }}>{q.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSelectedTemplate(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function InterviewerClients() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [clients, setClients] = useState<Client[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [interviewerTz, setInterviewerTz] = useState<string | undefined>(undefined)

  const clientIdParam = searchParams.get('client')
  const selectedClientId = clientIdParam && clientIdParam !== 'null' ? parseInt(clientIdParam) : null
  const targetBookingId = searchParams.get('booking') ? parseInt(searchParams.get('booking')!) : null

  useEffect(() => {
    interviewerApi
      .getClients()
      .then((data) => setClients(data || []))
      .catch(() => setError('Failed to load clients.'))
      .finally(() => setLoading(false))
    interviewerApi.getProfile().then((res) => {
      const p = res.data ?? res
      if (p.timezone) setInterviewerTz(p.timezone)
    }).catch(() => {})
  }, [])

  const selectedClient = selectedClientId
    ? clients.find((c) => c.user_id === selectedClientId) ?? null
    : null

  const clearClient = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('client')
    next.delete('prep')
    setSearchParams(next)
  }

  const openClient = (userId: number) => {
    setSearchParams({ prep: '1', client: String(userId) })
  }

  const filteredClients = clients.filter((c) => {
    const q = search.toLowerCase()
    return (
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.target_role || '').toLowerCase().includes(q)
    )
  })

  if (loading) return <div className="loading-screen">Loading clients...</div>

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Client Profiles</h1>
        <p className="page-subtitle">View client details, prepare question sets, and review session history</p>
      </div>

      {selectedClient ? (
        <div className="card ic-profile-card">
          <ClientProfile client={selectedClient} onClose={clearClient} interviewerTz={interviewerTz} targetBookingId={targetBookingId} />
        </div>
      ) : (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 20, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
            <Search size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              type="text"
              className="form-input"
              placeholder="Search clients by name, email, or target role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ border: 'none', boxShadow: 'none', padding: '4px 0' }}
            />
          </div>

          {error && <div className="empty-state"><Users size={48} /><h3>{error}</h3></div>}

          {!error && filteredClients.length === 0 && (
            <div className="empty-state">
              <Users size={48} />
              <h3>No clients found</h3>
              <p>Clients will appear here once they book an interview session with you.</p>
            </div>
          )}

          {!error && filteredClients.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filteredClients.map((client) => {
                const displayName = client.full_name || client.email || 'Unknown Client'
                return (
                  <div
                    key={client.user_id}
                    style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', border: '1px solid var(--border)', borderRadius: 12, cursor: 'pointer', background: 'white', transition: 'box-shadow 0.15s' }}
                    onClick={() => openClient(client.user_id)}
                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)')}
                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
                  >
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--primary-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, flexShrink: 0 }}>
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 4 }}>{displayName}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Briefcase size={13} /> {client.target_role || 'No target role set'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary-navy)' }}>{client.total_sessions}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sessions</div>
                    </div>
                    {client.last_session_at && (
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-dark)' }}>{formatShortDate(client.last_session_at)}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Last Session</div>
                      </div>
                    )}
                    <ChevronDown size={18} style={{ color: 'var(--text-muted)', transform: 'rotate(-90deg)', flexShrink: 0 }} />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        /* ── Client Header ── */
        .ic-client-hdr {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 28px;
          padding: 0 0 20px;
          border-bottom: 1px solid var(--border);
          flex-wrap: wrap;
        }
        .ic-client-stats {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
          flex-wrap: wrap;
        }

        /* ── Upcoming header ── */
        .ic-upcoming-hdr {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }

        /* ── Session card row ── */
        .ic-session-row {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .ic-session-info {
          flex: 1;
          min-width: 0;
        }
        .ic-session-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        /* ── Prep grid (desktop: sidebar + main) ── */
        .ic-prep-grid {
          display: grid;
          grid-template-columns: 340px 1fr;
          gap: 0;
        }

        /* ── Tablet (≤1024px): single column ── */
        @media (max-width: 1024px) {
          .ic-prep-grid { grid-template-columns: 1fr; }
          .ic-prep-grid > div:first-child { border-right: none !important; border-bottom: 1px solid var(--border); }
        }

        /* ── Mobile (≤640px) ── */
        @media (max-width: 640px) {
          /* Header: avatar + name row, then stats row below */
          .ic-client-hdr { gap: 12px; }
          .ic-client-stats {
            width: 100%;
            justify-content: flex-start;
            gap: 10px;
          }
          .ic-client-stats .btn {
            flex: 1;
            justify-content: center;
            font-size: 12px;
            padding: 7px 10px;
          }

          /* Upcoming header: title on first line, tip on second */
          .ic-upcoming-hdr { gap: 6px; }
          .ic-upcoming-hdr > div:last-child {
            font-size: 11px;
          }

          /* Session row: icon+info on first line, actions on second */
          .ic-session-row { flex-wrap: wrap; gap: 10px; }
          .ic-session-info { min-width: 0; flex: 1 1 0; }
          .ic-session-actions {
            width: 100%;
            justify-content: space-between;
          }
          .ic-session-actions .btn {
            flex: 1;
            justify-content: center;
          }

          /* Prep grid padding */
          .ic-prep-grid > div { padding: 16px !important; }
        }

        /* ── Outer card padding (ClientProfile host) ── */
        .ic-profile-card { padding: 28px; }
        @media (max-width: 640px) { .ic-profile-card { padding: 20px; } }
        @media (max-width: 480px) { .ic-profile-card { padding: 14px 12px; } }

        /* ── Previously Saved Question Sets row ── */
        .ic-prev-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          cursor: pointer;
        }
        .ic-prev-meta {
          font-size: 12px;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        /* ── Completed Sessions row ── */
        .ic-comp-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          cursor: pointer;
        }
        .ic-comp-meta {
          font-size: 12px;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          margin-top: 3px;
        }
        .ic-comp-badges {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        /* ── QuestionSetBuilder save button row ── */
        .ic-qs-save-row {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          flex-wrap: wrap;
        }

        /* ── Small mobile (≤480px) ── */
        @media (max-width: 480px) {
          .ic-client-hdr { gap: 10px; padding-bottom: 16px; margin-bottom: 20px; }
          .ic-client-stats > div { font-size: 13px; }
          .ic-client-stats > div > div:first-child { font-size: 20px !important; }
          .ic-upcoming-hdr > div:first-child h3 { font-size: 14px !important; }
          .ic-prep-grid > div { padding: 12px !important; }

          /* Previously Saved: smaller padding */
          .ic-prev-row { padding: 12px 14px; gap: 10px; }
          /* Completed Sessions: smaller padding, badges wrap */
          .ic-comp-row { padding: 12px 14px; gap: 10px; }
          .ic-comp-badges { flex-wrap: wrap; gap: 4px; }
          /* Save row: full-width buttons */
          .ic-qs-save-row { justify-content: stretch; }
          .ic-qs-save-row .btn { flex: 1; justify-content: center; }
        }
      `}</style>
    </div>
  )
}
